import { sha256 } from "@noble/hashes/sha2.js";
import { bytesToHex } from "@noble/hashes/utils.js";

import { authoredCanvasPublicationInput } from "../../../../../backend/convex/lib/canvasPublicationInput.js";
import { stepsForCall } from "../../../../../backend/convex/lib/processSteps.js";
import { normalizePreviewUrl } from "./preview.ts";

/**
 * What a tool's reviewed process declaration says about one call, and the digest of that exact call: the one law the
 * Desk's server (apps/server toolGates.ts, which re-exports these) and a window (steps 5-7 design §12a; step 7.4.2) both
 * read, so a gesture attested from either opens the same step for the same target the World's door recomputes.
 * Browser-safe: no Node module (the digest is @noble/hashes' sha256, byte for byte the same as node:crypto's).
 */

export type GateKind =
  | "HUMAN_IN_THE_LOOP"
  | "HUMAN_ON_THE_LOOP"
  | "HUMAN_OBSERVATION"
  | "HUMAN_INPUT";

export const NO_DECISION_RECEIPT_GATE_KEY = "HO-ACT-RECEIPT-01";
export const OPEN_GATE_INSTANCE_TOOL = "neucleos_open_gate_instance";
export const DECIDE_GATE_INSTANCE_TOOL = "neucleos_decide_gate_instance";
export const READ_PROCESS_BINDING_TOOL = "neucleos_read_process_binding";

const KINDS: ReadonlySet<string> = new Set([
  "HUMAN_IN_THE_LOOP",
  "HUMAN_ON_THE_LOOP",
  "HUMAN_OBSERVATION",
  "HUMAN_INPUT",
]);
export const isGateKind = (value: unknown): value is GateKind =>
  typeof value === "string" && KINDS.has(value);

/**
 * GTB4: what a reviewed process declaration says about one tool. A declared
 * gate carries its own key and catalog default; an explicit no-decision
 * declaration (decisions.kind "none") acts under the receipt gate so the act
 * is still recorded — an explicit absence of gates is never a silent allow.
 */
export interface DeclaredToolGate {
  readonly gateKey: string;
  readonly defaultKind: GateKind;
  readonly noDecision: boolean;
  /** The declared step this call's gate is: "<processKey>/<stepId>", governed by its own settings over its class's. */
  readonly stepKey?: string;
  readonly stepId?: string;
  /**
   * The call matched no declared step and its declaration does not say it needs none (or the declaration
   * is ambiguous): the person is asked under the declaration's strictest step, HUMAN_IN_THE_LOOP, and
   * decides it alone. The open says so (askPerson), so the World opens it that way.
   */
  readonly askPerson?: true;
}

/** "<processKey>/<stepId>" for a declared step, when the answer names its process and the step its ID. */
export function stepKeyOf(
  bindingAnswer: unknown,
  step: Record<string, unknown>,
): string | undefined {
  const processKey =
    bindingAnswer && typeof bindingAnswer === "object"
      ? (bindingAnswer as Record<string, unknown>)["processKey"]
      : undefined;
  return typeof processKey === "string" && typeof step["stepId"] === "string"
    ? `${processKey}/${step["stepId"]}`
    : undefined;
}

/**
 * The World tool a harness tool binds to, on any engine. The Claude SDK names
 * the Cleo MCP server's tools mcp__cleo__<name>; Codex names them
 * mcp:cleo:<name>; declarations and the World door know the bare World name.
 * Any other tool (native Write, a provider approval method, another server's
 * tool) is itself.
 */
export function declarationKeyForTool(toolName: string): string {
  // CodexAdapter names Cleo's MCP server cleo_ and twelve hex digits of a
  // digest of the Desk thread. Preserve every other server's namespace.
  const scoped = /^(?:mcp:cleo_[0-9a-f]{12}:|mcp__cleo_[0-9a-f]{12}__)(.+)$/.exec(toolName);
  if (scoped?.[1]) return scoped[1];
  for (const prefix of ["mcp__cleo__", "mcp:cleo:"]) {
    if (toolName.startsWith(prefix) && toolName.length > prefix.length)
      return toolName.slice(prefix.length);
  }
  return toolName;
}

/**
 * The facts the World answered beside a declaration that some of its conditions read
 * (backend/convex/lib/processSteps.js): the selected World's own website hostnames, from the World's records.
 * Absent, such a condition never holds, so the call asks.
 */
export function stepContextOfAnswer(answer: unknown): {
  readonly worldSiteHosts?: ReadonlyArray<string>;
} {
  const hosts =
    answer && typeof answer === "object"
      ? (answer as Record<string, unknown>)["worldSiteHosts"]
      : undefined;
  return Array.isArray(hosts) && hosts.every((host) => typeof host === "string")
    ? { worldSiteHosts: hosts as ReadonlyArray<string> }
    : {};
}

/** The call's `url` argument, as the step conditions read it (top level, or its request object's). */
function callUrl(input: unknown): unknown {
  if (!input || typeof input !== "object" || Array.isArray(input)) return undefined;
  const args = input as Record<string, unknown>;
  if (Object.prototype.hasOwnProperty.call(args, "url")) return args["url"];
  const nested = args["request"];
  return nested && typeof nested === "object" && !Array.isArray(nested)
    ? (nested as Record<string, unknown>)["url"]
    : undefined;
}

/** Whether the preview browser loads this address exactly as written: normalizePreviewUrl returns it unchanged, or
 * (a bare origin, "https://shop.example.com") only adds the root "/". Anything else, or no string, is not. */
function loadsAsWritten(url: unknown): boolean {
  if (typeof url !== "string") return false;
  let loaded: string;
  try {
    loaded = normalizePreviewUrl(url);
  } catch {
    return false;
  }
  if (loaded === url) return true;
  try {
    const origin = new URL(loaded).origin;
    return url === origin && loaded === `${origin}/`;
  } catch {
    return false;
  }
}

/**
 * Pull the declared gate out of neucleos_read_process_binding's answer; null
 * when undeclared or unreadable. Given the call (Arc ONE step 3), the gate is
 * the first step that applies to THIS call by the shared rule the World
 * enforces (backend/convex/lib/processSteps.js stepsForCall): a call its
 * declaration says needs no decision (a read named in noneFor) proceeds with
 * a receipt, as a no-decision declaration does. A call no step names
 * otherwise, or an ambiguous declaration's (several unmapped steps), is asked
 * of the person under the declaration's strictest step (askPerson; 4 October
 * 2026, known hole 6): before, it ran with only a receipt. Without the call,
 * the first gate.
 */
export function declaredToolGateFromDoorAnswer(
  answer: unknown,
  call?: { readonly toolName: string; readonly toolInput: unknown },
): DeclaredToolGate | null {
  if (!answer || typeof answer !== "object") return null;
  const row = answer as Record<string, unknown>;
  if (row["status"] !== "admitted") return null;
  const declaration = row["declaration"];
  if (!declaration || typeof declaration !== "object") return null;
  const decisions = (declaration as Record<string, unknown>)["decisions"];
  if (!decisions || typeof decisions !== "object") return null;
  const kind = (decisions as Record<string, unknown>)["kind"];
  if (kind === "none")
    return {
      gateKey: NO_DECISION_RECEIPT_GATE_KEY,
      defaultKind: "HUMAN_ON_THE_LOOP",
      noDecision: true,
    };
  if (kind !== "gates") return null;
  const gates = (decisions as Record<string, unknown>)["gates"];
  if (!Array.isArray(gates)) return null;
  if (call) {
    const toolKey = declarationKeyForTool(call.toolName);
    const context = stepContextOfAnswer(answer);
    let resolved = stepsForCall(declaration, call.toolInput, toolKey, context);
    // PR #1717: a load waved through only because its address is the World's own website must be loaded exactly as
    // judged. The preview browser loads normalizePreviewUrl(url); when that differs from the url judged, the call is
    // decided as if the World named no hostnames, so it asks.
    if (resolved.need === "none" && context.worldSiteHosts !== undefined) {
      const withoutSites = stepsForCall(declaration, call.toolInput, toolKey, {});
      if (withoutSites.need !== "none" && !loadsAsWritten(callUrl(call.toolInput)))
        resolved = withoutSites;
    }
    if (resolved.need === "none")
      return {
        gateKey: NO_DECISION_RECEIPT_GATE_KEY,
        defaultKind: "HUMAN_ON_THE_LOOP",
        noDecision: true,
      };
    const step = resolved.steps.find(
      (entry: Record<string, unknown>) =>
        typeof entry["gateKey"] === "string" && isGateKind(entry["defaultKind"]),
    );
    if (step) {
      const stepKey = stepKeyOf(answer, step);
      const ask = resolved.need === "ask";
      return {
        gateKey: step["gateKey"] as string,
        defaultKind: ask ? "HUMAN_IN_THE_LOOP" : (step["defaultKind"] as GateKind),
        noDecision: false,
        ...(stepKey ? { stepKey, stepId: step["stepId"] as string } : {}),
        ...(ask ? { askPerson: true as const } : {}),
      };
    }
  }
  for (const gate of gates) {
    if (!gate || typeof gate !== "object") continue;
    const entry = gate as Record<string, unknown>;
    if (typeof entry["gateKey"] === "string" && isGateKind(entry["defaultKind"]))
      return { gateKey: entry["gateKey"], defaultKind: entry["defaultKind"], noDecision: false };
  }
  return null;
}

/** sha256 of a UTF-8 string, lowercase hex. */
export function sha256Hex(text: string): string {
  return bytesToHex(sha256(new TextEncoder().encode(text)));
}

/**
 * The exact effect, digested: the World tool name plus its canonical input.
 * One law on every engine (the engine's spelling of the tool is normalised
 * through declarationKeyForTool), so the World's own door can recompute the
 * digest from the name and input it receives and find the instance the
 * harness opened instead of opening a second one.
 */
export function toolTargetDigest(toolName: string, toolInput: unknown): string {
  const canonical = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(canonical);
    if (value && typeof value === "object") {
      return Object.fromEntries(
        Object.keys(value as Record<string, unknown>)
          .sort()
          .filter((key) => (value as Record<string, unknown>)[key] !== undefined)
          .map((key) => [key, canonical((value as Record<string, unknown>)[key])]),
      );
    }
    return value;
  };
  return sha256Hex(
    JSON.stringify({
      toolName: declarationKeyForTool(toolName),
      input: canonical(
        declarationKeyForTool(toolName) === "neucleos_canvas"
          ? authoredCanvasPublicationInput(toolInput)
          : toolInput,
      ),
    }),
  );
}
